import React, { useEffect, useState } from "react";
import { Ruler, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DEPT_LABELS, CLOTHING_SIZES, WAIST_SIZES, INSEAM_SIZES, SHOE_AUDIENCES, SHOE_AUDIENCE_LABELS, SHOE_SIZES } from "@/lib/taxonomy";
import { effectivePrefs, saveLocalPrefs, emptyPrefs } from "@/lib/sizePrefs";

const Picker = ({ label, value, options, onChange, testid }) => (
  <label className="block">
    <span className="text-xs text-neutral-500">{label}</span>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="mt-1 w-full hairline p-2.5 text-sm bg-white">
      <option value="">Any</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

export default function MySizes() {
  const { user, reload } = useAuth();
  const [prefs, setPrefs] = useState(emptyPrefs());
  const [saving, setSaving] = useState(false);

  useEffect(() => { setPrefs(effectivePrefs(user)); }, [user]);

  const setClothing = (dept, field, val) =>
    setPrefs((p) => ({ ...p, clothing: { ...p.clothing, [dept]: { ...(p.clothing[dept] || {}), [field]: val } } }));
  const setShoe = (aud, val) =>
    setPrefs((p) => ({ ...p, shoes: { ...p.shoes, [aud]: val } }));

  const save = async () => {
    setSaving(true);
    saveLocalPrefs(prefs);
    try {
      if (user) {
        await api.put("/me/size-prefs", { clothing: prefs.clothing, shoes: prefs.shoes });
        await reload();
        toast.success("Sizes saved to your account");
      } else {
        toast.success("Sizes saved on this device");
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not save to your account");
    }
    setSaving(false);
  };

  return (
    <div className="max-w-[820px] mx-auto px-6 py-16" data-testid="my-sizes-page">
      <div className="flex items-center gap-2"><Ruler size={16} style={{ color: "#1E3A2B" }} /><span className="label-eyebrow">My Sizes</span></div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Shop what fits.</h1>
      <p className="mt-3 text-neutral-600 text-sm max-w-[60ch]">
        Save your sizes once and we'll pre-filter Women, Men, Kids and Shoes to your fit every time you browse — you can always clear it for a single visit.
        {!user && <span className="block mt-1 text-neutral-400">You're not signed in — sizes are saved on this device. Sign in to keep them across devices.</span>}
      </p>

      {["women", "men"].map((dept) => (
        <section key={dept} className="mt-10">
          <h2 className="font-display text-xl tracking-tight">{DEPT_LABELS[dept]}'s clothing</h2>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Picker label="Top / dress size" value={prefs.clothing[dept]?.top} options={CLOTHING_SIZES[dept]}
              onChange={(v) => setClothing(dept, "top", v)} testid={`size-${dept}-top`} />
            <Picker label="Pants waist" value={prefs.clothing[dept]?.waist} options={WAIST_SIZES[dept]}
              onChange={(v) => setClothing(dept, "waist", v)} testid={`size-${dept}-waist`} />
            <Picker label="Pants length (inseam)" value={prefs.clothing[dept]?.inseam} options={INSEAM_SIZES[dept]}
              onChange={(v) => setClothing(dept, "inseam", v)} testid={`size-${dept}-inseam`} />
          </div>
        </section>
      ))}

      <section className="mt-10">
        <h2 className="font-display text-xl tracking-tight">Kids' clothing</h2>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Picker label="Size" value={prefs.clothing.kids?.top} options={CLOTHING_SIZES.kids}
            onChange={(v) => setClothing("kids", "top", v)} testid="size-kids-top" />
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-xl tracking-tight">Shoe sizes</h2>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {SHOE_AUDIENCES.map((a) => (
            <Picker key={a.key} label={SHOE_AUDIENCE_LABELS[a.key]} value={prefs.shoes?.[a.key]} options={SHOE_SIZES[a.key]}
              onChange={(v) => setShoe(a.key, v)} testid={`shoe-size-${a.key}`} />
          ))}
        </div>
      </section>

      <div className="mt-10 flex items-center gap-4">
        <button className="btn-primary" onClick={save} disabled={saving} data-testid="save-sizes-btn">
          {saving ? "Saving…" : <><Check size={15} /> Save my sizes</>}
        </button>
        <button className="btn-outline" data-testid="clear-sizes-btn"
          onClick={() => { setPrefs(emptyPrefs()); saveLocalPrefs(emptyPrefs()); }}>Reset all</button>
      </div>
    </div>
  );
}
